import React, { useState } from 'react';
// --- THIS IS THE FIX ---
// This path is the full relative path, which should resolve the error.
import { useAuth } from '../context/AuthContext.js'; 
// --- END OF FIX ---
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { pushRedditConversionEvent } from '../utils/redditTracking.js';

// --- Reusable UI Components (to match your project) ---
const Input = ({ type, placeholder, value, onChange, ...props }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
    {...props}
  />
);

const Button = ({ children, onClick, loading, variant = 'primary', ...props }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className={`w-full px-4 py-3 font-semibold rounded-lg transition-colors
      ${variant === 'primary' 
        ? 'bg-orange-500 text-white hover:bg-orange-600 disabled:bg-gray-400'
        : 'bg-gray-600 text-white hover:bg-gray-700 disabled:bg-gray-400'
      }
    `}
    {...props}
  >
    {loading ? 'Loading...' : children}
  </button>
);

const Alert = ({ message }) => (
  <div className="flex items-center p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
    <AlertCircle className="w-5 h-5 mr-3" />
    <span className="font-medium">{message}</span>
  </div>
);
// --- End of Reusable UI Components ---


export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const { signUp } = useAuth(); // Get the signUp function
  const navigate = useNavigate();

  const handleSignUp = async (e) => {
    e.preventDefault();
    pushRedditConversionEvent({
      event: "sign_up",
      userData: email ? { email_address: email } : undefined,
      eventData: {
        action: "signup",
        source: "email_form",
      },
    });
    // 1. Check if passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    
    setError('');
    setMessage('');
    setLoading(true);

    // 2. Call the signUp function with all the new fields
    const { error } = await signUp(email, password, firstName, lastName);
    
    if (error) {
      setError(error.message);
    } else {
      setMessage('Sign up successful! Please check your email to verify your account.');
    }
    setLoading(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#F5F3F1]">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-xl">
        <div className="text-center">
          <Link to="/">
            <h2 className="text-3xl font-bold text-gray-900">
              SnapGrad
            </h2>
          </Link>
          <p className="mt-2 text-gray-600">Create your new account</p>
        </div>
        
        {error && <Alert message={error} />}
        {message && (
          <div className="p-4 text-sm text-green-700 bg-green-100 rounded-lg">
            {message}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSignUp}>
          {/* --- NEW FIELDS --- */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">First Name</label>
              <Input
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Last Name</label>
              <Input
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>
          {/* --- END NEW FIELDS --- */}

          <div>
            <label className="text-sm font-medium text-gray-700">Email address</label>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="relative">
            <label className="text-sm font-medium text-gray-700">Password</label>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 top-6 pr-3 flex items-center text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          {/* --- NEW CONFIRM PASSWORD FIELD --- */}
          <div className="relative">
            <label className="text-sm font-medium text-gray-700">Confirm Password</label>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          {/* --- END NEW FIELD --- */}


          <div className="flex flex-col space-y-4 pt-2">
            <Button type="submit" loading={loading}>
              Create Account
            </Button>
            
            <div className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:underline">
                Sign In
              </Link>
            </div>
            
          </div>
        </form>
      </div>
    </div>
  );
}
